import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { ContentSchemaRender } from "~/components/content/content-schema-render";
import { checkSchemaNodeHasScmpYoutubeVideo } from "~/components/content/content-schema-render/iframe/helpers";
import { useContentSchemaRenderIframe } from "~/components/content/content-schema-render/iframe/hooks";
import type { articleLeadingArticle$key } from "~/queries/__generated__/articleLeadingArticle.graphql";

import type { Props as ArticleLeadingImagesProps } from "./article-leading-images";
import { ArticleLeadingImages } from "./article-leading-images";
import { ArticleLeadingVideoContainer } from "./styles";

export type Props = {
  className?: string;
  reference: articleLeadingArticle$key;
} & Omit<ArticleLeadingImagesProps, "reference" | "imageProps">;

export const ArticleLeading: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  ...articleLeadingProps
}) => {
  const article = useFragment(
    graphql`
      fragment articleLeadingArticle on Article {
        displaySlideShow
        multimediaEmbed {
          json
        }
        ...articleLeadingImagesContent
        ...contentSchemaRenderContent
      }
    `,
    reference_,
  );

  const { displaySlideShow, multimediaEmbed } = article;

  // Check multimediaEmbed has youtube video in the node
  const isYoutubeVideo = useMemo(() => {
    if (!multimediaEmbed?.json) return false;
    return multimediaEmbed?.json?.some(node => checkSchemaNodeHasScmpYoutubeVideo(node)) ?? false;
  }, [multimediaEmbed]);

  const { ComponentWithExtraProps: IframeWithExtraProps } = useContentSchemaRenderIframe({
    gaActionPrefix: "Youtube article header/",
  });

  if (isYoutubeVideo)
    return (
      <ArticleLeadingVideoContainer className={className}>
        <ContentSchemaRender
          extraComponentMap={{
            iframe: IframeWithExtraProps,
          }}
          reference={article}
          schema={multimediaEmbed?.json}
        />
      </ArticleLeadingVideoContainer>
    );

  if (!displaySlideShow) return null;

  return (
    <ArticleLeadingImages
      {...articleLeadingProps}
      className={className}
      imageProps={{ lazyload: false }}
      reference={article}
    />
  );
};

ArticleLeading.displayName = "ArticleLeading";
